/**
 * accounts (#2005) — the account dimension for the Liquiditätsplanung app.
 *
 * After #2001 a multi-bank tenant exposes N ``financial_account`` entities. This
 * pure module turns the flattened entity rows (attrs are TOP-LEVEL — the backend
 * ``_flatten_entity_row`` lifts the JSONB out of ``attributes``) into the app's
 * ``LiquidityAccount`` view models: the per-account engine config (own anchor +
 * limit), a display label (bank + IBAN last-4), and a stable colour for the
 * "Alle Konten" account-switcher dots. No React, no network.
 *
 * The legacy ``fa-liquiditaet`` alias (migrated single-bank tenants like Nina)
 * sorts first and is flagged ``isAlias`` — it is the only account the tenant-level
 * user plans / statements overlay onto (D2).
 *
 * Ordering (#2089): the alias/aggregate stays FIRST, then real accounts sort
 * **A–Z by bank name** (locale-aware German, case-insensitive), tie-broken by the
 * IBAN last-4, then by stable input order. This one sort is the single source of
 * truth for BOTH surfaces the account list feeds — the "Alle Konten" per-account
 * breakdown rows AND the top segmented-control chips (both map over this order via
 * ``accountSeries``) — so the two can never diverge.
 */
import { accountToConfig } from './cashFlowProjection';
import type { LiquiditaetConfig } from './types';

/** The migration's single-account alias id (kept for migrated tenants). */
export const LEGACY_ALIAS_ACCOUNT_ID = 'fa-liquiditaet';

/** The worker's per-tenant roll-up sentinel (``liquidity_projection`` grain). It
 *  is NOT a real bank account and must never appear in the selector — defensively
 *  filtered here in case it ever surfaces as a ``financial_account`` row. */
const TENANT_TOTAL_ACCOUNT_ID = '__tenant_total__';

/**
 * The largest per-tenant Konto count anyone has observed in production — WF, per
 * #3036, which is also the tenant whose folded account list made all N dots
 * reachable again.
 *
 * Exported so it can be ASSERTED rather than remembered: ``__tests__/accounts.test.ts``
 * requires ``ACCOUNT_COLORS`` to be strictly longer than this. When a tenant with
 * more accounts turns up, raising this number reds that test until the palette
 * grows with it — which is the point. The alternative is what shipped until
 * #2987: an unstated assumption, exceeded in production, expressed on screen as
 * two banks wearing one colour.
 */
export const LARGEST_OBSERVED_ACCOUNT_COUNT = 11;

/**
 * App-local palette for the per-account switcher dots (Alle Konten view).
 * Deliberately app-local, not a design-system token — only this app colours N
 * account rows in one switcher. The Alle-Konten Summe row keeps the primary blue.
 *
 * TWELVE SLOTS, AND EVERY PAIR IS MEASURED (#2987). The six that stood here until
 * #2987 had never been validated, and they failed hard once they were: all-pairs
 * deutan ΔE **1,23** on ``#5b8c5a``↔``#b5645a`` (the same colour to a
 * deuteranope, and byte-for-byte the number #2955 was filed to fix for the chart
 * series), all-pairs normal-vision ΔE **7,49** on ``#3b7ea1``↔``#6b8e9c`` — a
 * collision for readers with no deficiency at all — and four of the six under the
 * OKLCH chroma floor. This set clears, over ALL 66 pairs rather than only adjacent
 * ones: worst CVD ΔE 6,24 · worst normal-vision ΔE 15,45 · every slot inside the
 * light-mode L band 0,43–0,77, above chroma 0,10, and over WCAG 1.4.11's 3:1
 * against the white card it is painted on. The numbers are pinned in
 * ``__tests__/chartColors.test.ts`` (which owns the CVD transforms); re-run them
 * and argue a new number rather than re-baselining a pin.
 *
 * WHY ALL-PAIRS, not the validator's adjacent-pair default: ``colorIndex`` comes
 * from the #2089 A–Z sort and the page's Konto filter decides which rows are on
 * screen, so any two slots can end up neighbours. Adjacency is data-dependent
 * here in a way it is not for a fixed chart series.
 *
 * WHY TWELVE, not six re-hexed: ``accountColor`` cycles, so at six slots a
 * WF-scale tenant (~11 Konten) ALIASED in production — ``accountColor(6)``
 * returned slot 0 with nothing on screen to say the two rows meant different
 * banks. Twelve clears ``LARGEST_OBSERVED_ACCOUNT_COUNT`` with one slot spare.
 * Rejected alternatives, both considered: capping the number of coloured dots
 * (fights #3036, which just made the whole list reachable) and generating colours
 * at runtime (cannot be CVD-validated by a static guard, which is the entire
 * point).
 *
 * WHY IT IS NO LONGER "MUTED", which the previous comment promised: twelve
 * categorical hues that pairwise clear both floors inside one lightness band do
 * not exist at low chroma — the peak here is OKLCH C 0,23 against the old
 * palette's 0,12. The arithmetic simply does not leave a muted twelve-set, and
 * that trade is deliberate: an under-saturated slot is a slot two readers cannot
 * tell apart. The two canonical published CVD-safe schemes were both measured
 * before searching for these: Okabe-Ito clears the separation gates but is only
 * EIGHT colours and breaks the light-mode L band and 3:1 contrast (its yellow
 * sits at 1,32:1 on white); Paul Tol's *muted* nine reaches all-pairs CVD ΔE 5,18
 * and normal 12,90, i.e. it fails both. Neither could be adopted as-is.
 *
 * ORDER IS LOAD-BEARING: farthest-first, so every PREFIX is itself a
 * maximally-separated subset. Most tenants have two or three Konten and see only
 * the first slots, so the six-account case measures BETTER (worst CVD 6,77) than
 * the full twelve rather than inheriting the worst pair of a list it never
 * reaches. Reordering these is therefore a measured change, not a cosmetic one.
 */
export const ACCOUNT_COLORS = [
  '#cc60f6',
  '#486012',
  '#6624ae',
  '#f0606c',
  '#309c66',
  '#065a8a',
  '#a2722a',
  '#7e365a',
  '#a2669c',
  '#2a5aea',
  '#4896c6',
  '#c01200',
] as const;

/**
 * The switcher-dot colour for a display index (``LiquidityAccount.colorIndex``).
 *
 * ALIASES PAST THE SLOT COUNT, deterministically and on purpose. The wrap is a
 * plain modulo — index 12 reuses slot 0, negative indices land in range too (JS
 * ``%`` keeps the sign of its left operand, hence the double modulo) — so the
 * mapping is total and stable across renders, which matters because a colour that
 * changed between paints would be worse than one that repeats. It is bounded, not
 * removed: for an unbounded account count no finite validated palette can be, and
 * generating one at runtime would forfeit the static validation this palette
 * exists to have.
 *
 * What makes the aliasing acceptable rather than merely inevitable is that it is
 * (a) beyond ``LARGEST_OBSERVED_ACCOUNT_COUNT``, asserted in
 * ``__tests__/accounts.test.ts`` so exceeding it fails a test instead of a
 * screen, (b) pinned there at the exact wrap index, and (c) never the sole
 * channel: every dot is ``aria-hidden`` and sits immediately beside its own
 * ``__breakdown-label`` (bank name + IBAN) in ``LiquiditySummary``, so two
 * aliased rows are still told apart by the text that names them.
 */
export function accountColor(i: number): string {
  const n = ACCOUNT_COLORS.length;
  return ACCOUNT_COLORS[((i % n) + n) % n];
}

export interface LiquidityAccount {
  /** The ``account_id`` (deterministic ``fa-*``). */
  id: string;
  bankName: string | null;
  ibanLast4: string | null;
  /** IBAN country code + check digits ("DE36") — disambiguates two same-bank
   *  accounts whose IBANs share the last 4 (WF Volksbank ···8880 look-alikes). */
  ibanFirst4: string | null;
  /** True for the migrated single-account ``fa-liquiditaet`` alias. */
  isAlias: boolean;
  /** The engine's overlay target for account-less user plans (D2): the alias if
   *  present, else the FIRST account in backend/input order. Computed BEFORE the
   *  #2089 display sort so the engine routing stays independent of the A–Z view
   *  order — ``partitionEngineRecords`` reads this flag, not ``accounts[0]``, so
   *  re-sorting the list for display never re-routes a plan (codex #2089). */
  isOverlayPrimary: boolean;
  /** The engine anchor/limit config off THIS account's ``opening_balance*`` /
   *  ``kontokorrent_limit`` (null when the row has no usable anchor). */
  config: LiquiditaetConfig | null;
  /** Palette index (by final alias-first order) — the switcher-dot colour. */
  colorIndex: number;
  /** The flattened entity row — kept so the hook can partition cash_flows by
   *  ``account_id`` without a second fetch. */
  entity: Record<string, unknown>;
}

/**
 * Is this flattened ``financial_account`` row a synthetic revenue bucket
 * (#2076 B9, #2636, #2664)?
 *
 * A structured-source backfill (wiberry booth revenue) can mint the
 * ``fa-liquiditaet`` alias as a sink for identity-less revenue aggregates. It is
 * analytics substrate, not a Konto: it has no balance anchor and its "flows"
 * duplicate money the bank statements already book as deposits. Excluding it is
 * deliberate (codex #2125 P1) — showing it as an account either dropped it
 * silently (anchor-less) or double-counted. Ontology: ``absent = ordinary account``.
 *
 * **Strict ``=== true``, deliberately** — mirror of the backend chokepoint
 * ``liquiditaet_projection.is_revenue_bucket``, whose docstring spells out the
 * hazard: on a transport that stringifies JSONB scalars a truthy test would treat
 * the string ``'false'`` as a bucket and so exclude a REAL bank account, silently
 * dropping its money from the balance. Missing a bucket leaves today's known bug;
 * dropping a Konto invents a worse one.
 *
 * Single chokepoint (#2664) so the account list and the single-account anchor pick
 * in ``useLiquiditaetsplanung`` can never disagree, and so there is exactly one
 * place to widen if a stringifying transport ever appears here.
 */
export function isRevenueBucket(entity: Record<string, unknown>): boolean {
  return entity.revenue_bucket === true;
}

/**
 * Is this row analytics substrate rather than a Konto — a synthetic revenue bucket
 * or the worker's per-tenant roll-up sentinel (#2664)?
 *
 * The two surfaces that pick accounts — this module's ``accountsFromEntities`` and
 * the single-account anchor pick in ``useLiquiditaetsplanung`` — must agree on what
 * is NOT an account, or the tenant's Konto list and its curve describe different
 * sets. They disagreed before #2664 (the anchor pick read the raw rows), which is
 * exactly how a bucket became a Konto; sharing one predicate makes that class of
 * drift unrepresentable rather than merely fixed.
 *
 * Deliberately does NOT assert the id's shape: ``accountsFromEntities`` additionally
 * requires a non-empty string ``account_id`` (it keys its view models on it), but the
 * anchor pick reads only ``opening_balance*``, so demanding an id there could blank a
 * curve that renders today. Excluding a synthetic row is safe; dropping a row that
 * might carry a real anchor is not.
 */
export function isSyntheticAccountRow(entity: Record<string, unknown>): boolean {
  return isRevenueBucket(entity) || entity.account_id === TENANT_TOTAL_ACCOUNT_ID;
}

/**
 * The ``account_id``s :func:`accountsFromEntities` dropped as revenue buckets —
 * minus any id that ALSO belongs to a surviving real account.
 *
 * Their ``cash_flow`` rows must be dropped WITH them (#2636/#2664). Filtering the
 * bucket out of the account list alone is not enough for a tenant whose ONLY
 * ``financial_account`` is the bucket: no account survives, so the single-account
 * feed in ``useLiquiditaetsplanung`` scopes with ``accountId == null`` — which
 * takes EVERY cash_flow, the bucket's included — and the very double-count the
 * filter removes reappears through a different branch. (The multi-account fold is
 * already safe: ``partitionEngineRecords`` scopes per surviving account id, and a
 * bucket id is never among them.)
 *
 * The subtraction is a fail-safe: never exclude flows belonging to an id that
 * SURVIVED the filter. Unreachable today (``entity_id`` is the PK and every writer
 * sets ``entity_id == account_id``, so two rows cannot share an ``account_id``),
 * but it makes "a real account's money can never vanish" a structural property
 * rather than an incidental one. Mirror of the backend
 * ``liquiditaet_projection.revenue_bucket_account_ids`` + its caller's subtraction.
 */
export function revenueBucketAccountIds(entities: Record<string, unknown>[]): Set<string> {
  const buckets = new Set<string>();
  const survivors = new Set<string>();
  for (const e of entities) {
    const id = e.account_id;
    if (typeof id !== 'string' || id.length === 0) continue;
    if (isRevenueBucket(e)) buckets.add(id);
    // "Survivor" means survivor of ``accountsFromEntities`` — which also drops the
    // roll-up sentinel, so the sentinel can never shield a bucket's flows here
    // either (mirror of the backend, which subtracts the ids of the accounts its
    // own filter returned).
    else if (id !== TENANT_TOTAL_ACCOUNT_ID) survivors.add(id);
  }
  for (const id of survivors) buckets.delete(id);
  return buckets;
}

const strOrNull = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

/** Locale-aware German collator: case- and accent-insensitive (``sensitivity:
 *  'base'`` folds ä→a, ß→ss, Ö→o) so "Öko" sorts near "Oko" and casing never
 *  reorders. ``numeric`` keeps the IBAN last-4 tiebreak in numeric order.
 *  Constructed once — cheaper than a per-comparison ``localeCompare``. */
const nameCollator = new Intl.Collator('de', { sensitivity: 'base', numeric: true });

/** Turn the flattened ``financial_account`` rows into ordered account view models. */
export function accountsFromEntities(
  entities: Record<string, unknown>[],
  wjStartMonth = 7,
): LiquidityAccount[] {
  const rows = entities.filter((e) => {
    const id = e.account_id;
    if (isSyntheticAccountRow(e)) return false;
    return typeof id === 'string' && id.length > 0;
  });

  // The engine overlay target for account-less user plans (D2), fixed from the
  // INPUT order BEFORE the display sort below: the alias if present, else the
  // first input-order account. Decoupling this from the A–Z view order keeps the
  // #2089 sort purely presentational — re-ordering the list can never re-route a
  // plan onto a different account's series (codex #2089).
  const overlayPrimaryId =
    rows.find((r) => r.account_id === LEGACY_ALIAS_ACCOUNT_ID)?.account_id ?? rows[0]?.account_id;

  // Alias/aggregate FIRST, then A–Z by bank name (locale-aware German,
  // case-insensitive), tie-broken by the IBAN last-4, then by stable input order
  // (``sort`` is not a stable-order guarantee across engines, so rank explicitly
  // by index as the final key). #2089.
  const ranked = rows
    .map((entity, index) => ({ entity, index }))
    .sort((a, b) => {
      const aAlias = a.entity.account_id === LEGACY_ALIAS_ACCOUNT_ID ? 0 : 1;
      const bAlias = b.entity.account_id === LEGACY_ALIAS_ACCOUNT_ID ? 0 : 1;
      if (aAlias !== bAlias) return aAlias - bAlias;
      const nameCmp = nameCollator.compare(
        strOrNull(a.entity.bank_name) ?? '',
        strOrNull(b.entity.bank_name) ?? '',
      );
      if (nameCmp !== 0) return nameCmp;
      const last4Cmp = nameCollator.compare(
        strOrNull(a.entity.iban_last4) ?? '',
        strOrNull(b.entity.iban_last4) ?? '',
      );
      if (last4Cmp !== 0) return last4Cmp;
      return a.index - b.index;
    });

  return ranked.map(({ entity }, colorIndex) => {
    const id = entity.account_id as string;
    return {
      id,
      bankName: strOrNull(entity.bank_name),
      ibanLast4: strOrNull(entity.iban_last4),
      ibanFirst4: strOrNull(entity.iban_first4),
      isAlias: id === LEGACY_ALIAS_ACCOUNT_ID,
      isOverlayPrimary: id === overlayPrimaryId,
      config: accountToConfig(entity, wjStartMonth),
      colorIndex,
      entity,
    };
  });
}

/** The abbreviated IBAN: "DE36···8880" when the prefix is known, else "···8880".
 *  The prefix (country + check digits) is what tells two same-bank accounts with
 *  the same last-4 apart. */
const maskedIban = (a: LiquidityAccount): string | null =>
  a.ibanLast4 ? `${a.ibanFirst4 ?? ''}···${a.ibanLast4}` : null;

/** Full label for the Alle-Konten breakdown row: "{bank} DE36···{last4}" → fallbacks. */
export function accountLabel(a: LiquidityAccount): string {
  const iban = maskedIban(a);
  if (a.bankName && iban) return `${a.bankName} ${iban}`;
  if (a.bankName) return a.bankName;
  if (iban) return iban;
  if (a.isAlias) return 'Hauptkonto';
  return `Konto ···${a.id.slice(-4)}`;
}

/** Compact label for the segmented control: "DE36···{last4}" → fallbacks. */
export function accountShortLabel(a: LiquidityAccount): string {
  const iban = maskedIban(a);
  if (iban) return iban;
  if (a.bankName) return a.bankName;
  if (a.isAlias) return 'Hauptkonto';
  return `···${a.id.slice(-4)}`;
}
